import { useEffect, useState } from "react";
import { BellRing, Lock } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, LEVEL_LABEL, LEVEL_RANK } from "@/lib/api";
import { LevelPicker } from "@/components/AdminClients";

const fmt = (d) => new Date(d).toLocaleString("es-MX", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });

export const AdminAccessRequests = ({ data, onSeen }) => {
  const [items, setItems] = useState(data?.items ?? []);
  useEffect(() => { setItems(data?.items ?? []); }, [data?.items]);
  useEffect(() => {
    if (!data?.unseen) return undefined;
    const t = setTimeout(() => { api.post("/admin/premium-requests/seen").then(onSeen).catch(() => {}); }, 1500);
    return () => clearTimeout(t);
  }, [data?.unseen, onSeen]);

  const setLevel = async (it, v) => {
    try {
      await api.patch(`/admin/users/${it.user_id}`, { subscription_level: v });
      setItems((all) => all.map((x) => (x.user_id === it.user_id ? { ...x, subscription_level: v } : x)));
      toast.success(`${it.name}: ${LEVEL_LABEL[v]}`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <section className="mt-10" data-testid="admin-access-requests">
      <p className="text-sm text-[#F5F1E8]/70 max-w-2xl">Clientes que abrieron un módulo bloqueado. Contáctalos y, al confirmar el pago, asígnales el plan aquí mismo.</p>
      <div className="mt-6 border-t border-white/10">
        {items.length === 0 && <div className="py-12 flex items-center gap-3 text-[#F5F1E8]/60" data-testid="access-requests-empty"><BellRing className="w-5 h-5" /> Aún no hay solicitudes de acceso.</div>}
        {items.map((it) => {
          const granted = (LEVEL_RANK[it.subscription_level] ?? 0) >= (LEVEL_RANK[it.required_level] ?? 0);
          return (
            <div key={`${it.user_id}-${it.module_id}`} className="grid grid-cols-12 gap-4 items-center py-5 px-2 border-b border-white/10" data-testid={`access-request-${it.user_id}-${it.module_id}`}>
              <div className="col-span-12 md:col-span-5 min-w-0">
                <div className="font-semibold truncate flex items-center gap-2">
                  {!it.seen && <span className="w-2 h-2 rounded-full bg-[#F59E0B] shrink-0" data-testid="access-request-new-dot" />}
                  {it.name} {it.company && <span className="text-[#F5F1E8]/55 font-normal">· {it.company}</span>}
                </div>
                <a href={`mailto:${it.email}`} className="font-mono text-xs text-[#F5F1E8]/60 hover:text-[#F59E0B]">{it.email}</a>
              </div>
              <div className="col-span-12 md:col-span-4 text-sm">
                <div className="flex items-center gap-1.5"><Lock className="w-3.5 h-3.5 text-[#F59E0B]" /> {it.module_name}</div>
                <div className="font-mono text-xs text-[#F5F1E8]/55 mt-1">Requiere {LEVEL_LABEL[it.required_level]} · {it.count} {it.count === 1 ? "vez" : "veces"} · {fmt(it.last_at)}</div>
              </div>
              <div className="col-span-12 md:col-span-3 flex md:justify-end items-center gap-2">
                {granted && <span className="text-xs font-semibold text-emerald-400">Acceso otorgado</span>}
                <LevelPicker value={it.subscription_level} onChange={(v) => setLevel(it, v)} testid={`access-level-${it.user_id}-${it.module_id}`} />
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};
